import { useIsFocused } from "expo-router/react-navigation";
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useOnBoardingChurch } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { useCreateManyPrograms } from '@/api/hooks/programHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import { CreateProgram } from '@/api/models/programModels';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import ProgramForm from '@/components/forms/ProgramForm';
import FileInputField from '@/components/inputs/FileInputField';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { ProgramData, ProgramsData, programsResolver } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

// Just for CHURCH type
const Programs = () => {
  const { programDataJson, programIndex } = useLocalSearchParams<{
    programDataJson: string;
    programIndex: string;
  }>();
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useOnBoardingChurch();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { mutateAsync: createManyPrograms, isPending: programsPending } = useCreateManyPrograms();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();
  const isFocused = useIsFocused();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');
  const initialData = {
    byLaws: typeof nonProfitOrg?.byLawsDoc === 'string' ? safeJsonParseArray(nonProfitOrg.byLawsDoc)[0] : '',
    programs:
      nonProfitOrg?.programs !== undefined ? safeJsonParse<ProgramData[]>(JSON.stringify(nonProfitOrg.programs)) : []
  };

  useLayoutEffect(() => {
    // Only Churches
    if (isFocused) setCurrentPage(13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (programDataJson) {
      const programData = safeJsonParse<ProgramData>(programDataJson);
      if (programData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !programIndex && !fields.find((field) => objectComparision(field, programData))
          ? append(programData)
          : update(+programIndex, programData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programDataJson]);

  const {
    control,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    getFieldState,
    formState: { isValid, submitCount, errors }
  } = useForm<ProgramsData>({
    defaultValues: {
      byLaws: initialData.byLaws,
      programs: initialData.programs
    },
    mode: 'onSubmit',
    resolver: programsResolver as any
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'programs'
  });

  const updateNonProfit = ({ byLaws, programs }: { byLaws: string[] | null; programs: CreateProgram[] }) => {
    try {
      if (byLaws === null) {
        createManyPrograms(
          { programs: programs, npoId: authState.signUpData?.loginEmail?.npoId },
          {
            onSuccess: () => {
              onBoardingUpdate(
                {
                  preferenceId: authState.preferenceId ?? '',
                  programs: true
                },
                {
                  onSuccess: () => {
                    setIsLoading(false);
                    setFileProgress(undefined);
                    router.navigate('/(app)/login/nonProfit/onBoarding/lightRatingQuestions');
                  }
                }
              );
            },
            onError: (e) => {
              setIsLoading(false);
              setFileProgress(undefined);
              console.error(e);
            }
          }
        );
      } else {
        nonProfitUpdate(
          {
            npoId: authState.signUpData?.loginEmail?.npoId ?? '',
            byLaws: byLaws ?? undefined
          },
          {
            onSuccess: () => {
              createManyPrograms(
                { programs: programs, npoId: authState.signUpData?.loginEmail?.npoId },
                {
                  onSuccess: () => {
                    onBoardingUpdate(
                      {
                        preferenceId: authState.preferenceId ?? '',
                        programs: true
                      },
                      {
                        onSuccess: () => {
                          setIsLoading(false);
                          setFileProgress(undefined);
                          router.navigate('/(app)/login/nonProfit/onBoarding/lightRatingQuestions');
                        }
                      }
                    );
                  },
                  onError: (e) => {
                    setIsLoading(false);
                    setFileProgress(undefined);
                    console.error(e);
                  }
                }
              );
            },
            onError: (e) => {
              setIsLoading(false);
              console.error(e);
            }
          }
        );
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  const onSubmit: SubmitHandler<ProgramsData> = async (data) => {
    try {
      setIsLoading(true);

      const mappedData = data.programs.map<CreateProgram>((program) => ({
        ...program,
        nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
      }));

      if (data.byLaws && !containsSection(data.byLaws, 'byLaws')) {
        let fileToUpload: FileToUpload = {
          uri: data.byLaws,
          name: `by_laws.${new Date().toISOString()}`
        };

        await uploadToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/byLaws/`,
          fileToUpload,
          (progress) => setFileProgress(progress)
        ).then((storageFile) => {
          updateNonProfit({
            byLaws: [storageFile.metadata.fullPath],
            programs: mappedData
          });
        });
      } else {
        if (data.byLaws) {
          updateNonProfit({
            byLaws: null,
            programs: mappedData
          });
        } else {
          updateNonProfit({
            byLaws: [],
            programs: mappedData
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={100}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.programs.title')}
          infoButtonLabel={i18n.t('sign_in.nonProfit.programs.info')}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.programs.upload_form')}
            infoButtonLabel={i18n.t('sign_in.nonProfit.programs.upload_form')}>
            <FileInputField control={control} name="byLaws" progress={nonProfitIsPending ? fileProgress : undefined} />
          </OnboardingSection>
          {getValues('programs') && getValues('programs').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 100 - top - bottom }}>
              <ProgramForm
                initialData={getValues('programs.0')}
                showDoneButton
                showBackButton
                isLoading={onBoardingIsPending || programsPending}
                manualValidation={true}
                onDone={(data, triggerProgram) => {
                  if (!getFieldState('programs').isDirty) {
                    clearErrors();
                    handleSubmit(onSubmit)();
                    return;
                  }
                  if (data.name) {
                    clearErrors();
                    insert(0, data);
                    handleSubmit(onSubmit)();
                  } else if (
                    !data.name ||
                    data.description ||
                    data.budget ||
                    data.address ||
                    data.location ||
                    data.programDate ||
                    data.title
                  ) {
                    triggerProgram();
                  }
                }}
                onAdd={(data) => {
                  update(0, data);
                  trigger('programs.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/login/nonProfit/addUpdateProgram');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.next')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(program) => program.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/login/nonProfit/addUpdateProgram',
                        params: {
                          programDataJson: JSON.stringify(field.item),
                          programIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.name ?? ''}
                    subtitle={field.item.description ?? ''}
                    budget={field.item.budget}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/login/nonProfit/addUpdateProgram')}
                    label={i18n.t('sign_in.nonProfit.programs.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <View style={styles.buttonsContainer}>
          {getValues('programs') && getValues('programs').length > 0 && (
            <View style={styles.nextBackContainer}>
              {router.canGoBack() && (
                <BrandedButton
                  disabled={onBoardingIsPending || programsPending || nonProfitIsPending || isLoading}
                  style={styles.flexOne}
                  onPress={router.back}>
                  {capitalize(i18n.t('mix.back'))}
                </BrandedButton>
              )}
              <BrandedButton
                style={styles.flexOne}
                loading={onBoardingIsPending || programsPending || nonProfitIsPending || isLoading}
                disabled={!isValid && submitCount > 0}
                onPress={handleSubmit(onSubmit)}>
                {i18n.t('mix.next')}
              </BrandedButton>
            </View>
          )}
          <SaveAndContinueLaterButton disabled={onBoardingIsPending || programsPending || nonProfitIsPending} />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  divider: {
    marginBottom: 15
  }
});

export default Programs;
